import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton, ViewModeOption } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect } from 'components/ui';
import { useSnackbar } from 'components/common/SnackbarNotification';
import { LoanMappingViewModal } from 'components/modals/modules/loan-mapping-module/LoanMappingModal';
import type { LoanMappingRecord } from 'components/modals/modules/loan-mapping-module/LoanMappingModal';
import { formatCurrency, formatDate, getStatusBadgeStyle } from 'utils/lmmsHelpers';
import { useLoanMappingsPaged, useLoanMappingDetail } from 'hooks/api';
import { GridLoadingSkeleton } from 'components/common';
import type { MappedGL, LoanMappingFilterParams } from 'api/types/loanMapping.types';
import { loanMappingService } from 'api/services/loanMappingService';
// ============================================================================
// MAIN LOAN MAPPING SCREEN COMPONENT
// ============================================================================

const DEFAULT_PAGE_SIZE = 25;

const LoanMappingScreen: React.FC = () => {
  // Server-side query state (page + filters). The grid only ever holds the
  // current page — never the whole dataset — which is what keeps this screen
  // fast on slow connections.
  const [queryParams, setQueryParams] = useState<LoanMappingFilterParams>({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  });

  // API hooks
  const { data: apiData, pagination, loading: apiLoading, error: apiError, refetch } = useLoanMappingsPaged(queryParams);
  const { fetchDetail, loading: detailLoading } = useLoanMappingDetail();
  // Data from API (current page only)
  const mappings = (apiData as unknown as LoanMappingRecord[]) || [];
  // UI state
  const [viewMode, setViewMode] = useState<string>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState<LoanMappingRecord[]>([]);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<LoanMappingRecord | null>(null);

  // Filter draft state (applied to queryParams on "Apply")
  const [statusFilter, setStatusFilter] = useState('');

  // Debounced search → server-side searchTerm (matches customer name / id /
  // loan account / loan type / status on the backend).
  useEffect(() => {
    const timer = setTimeout(() => {
      setQueryParams((prev) => {
        const next = searchQuery || undefined;
        if (prev.searchTerm === next) return prev;
        return { ...prev, searchTerm: next, page: 1 };
      });
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Server-side pagination handlers
  const handlePageChange = useCallback((pageIndex: number) => {
    setQueryParams((prev) => ({ ...prev, page: pageIndex + 1 })); // API is 1-based
  }, []);
  const handlePageSizeChange = useCallback((size: number) => {
    setQueryParams((prev) => ({ ...prev, pageSize: size, page: 1 }));
  }, []);

  // Apply / clear the status filter (server-side)
  const handleApplyFilters = useCallback(() => {
    setQueryParams((prev) => ({ ...prev, page: 1, statusCode: statusFilter || undefined }));
    setFilterOpen(false);
  }, [statusFilter]);
  const handleClearFilters = useCallback(() => {
    setStatusFilter('');
    setQueryParams((prev) => ({ page: 1, pageSize: prev.pageSize, searchTerm: prev.searchTerm }));
  }, []);

  // GL detail panel state
  const [expandedMappingId, setExpandedMappingId] = useState<string | null>(null);
  const glCacheRef = useRef<Map<string, MappedGL[]>>(new Map());
  const [glPanelRows, setGlPanelRows] = useState<MappedGL[]>([]);
  const [glPanelLoading, setGlPanelLoading] = useState(false);

  const { showMessage } = useSnackbar();

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleGlCountClick = useCallback(async (mappingId: string) => {
    if (expandedMappingId === mappingId) {
      setExpandedMappingId(null);
      return;
    }
    setExpandedMappingId(mappingId);
    if (glCacheRef.current.has(mappingId)) {
      setGlPanelRows(glCacheRef.current.get(mappingId)!);
      return;
    }
    setGlPanelLoading(true);
    try {
      const res = await loanMappingService.getById(mappingId);
      const gls = res.data?.mappedGls ?? [];
      glCacheRef.current.set(mappingId, gls);
      setGlPanelRows(gls);
    } catch {
      showMessage('Failed to load GL details.', 'error');
      setExpandedMappingId(null);
    } finally {
      setGlPanelLoading(false);
    }
  }, [expandedMappingId, showMessage]);

  const handleView = useCallback(async (record: LoanMappingRecord) => {
    setSelectedRecord(record);
    setViewModalOpen(true);
    // Fetch detail to get mapped GLs
    const detail = await fetchDetail(record.id);
    if (detail) {
      setSelectedRecord({
        ...record,
        addonLoanAccountNumber: detail.addonLoanAccountNumber,
        addonLoanType: detail.addonLoanType,
        mappedGls: detail.mappedGls,
      });
    }
  }, [fetchDetail]);

  const handleRecordUpdated = useCallback((updated: LoanMappingRecord) => {
    setSelectedRecord(updated);
    // Invalidate GL cache for this mapping so the panel re-fetches fresh data
    glCacheRef.current.delete(updated.id);
  }, []);

  // ============================================================================
  // ROW ACTIONS
  // ============================================================================

  const getRowActions = useCallback((row: LoanMappingRecord): ActionMenuItem[] => [
    {
      id: 'view',
      label: 'View Details',
      icon: <IconSystem.eye size={18} />,
      onClick: () => handleView(row),
    },
  ], [handleView]);

  // ============================================================================
  // COLUMN DEFINITIONS
  // ============================================================================

  const tableColumns: SyutGridColumn<LoanMappingRecord>[] = useMemo(() => [
    {
      id: 'customerName',
      header: 'Customer',
      accessorKey: 'customerName',
      size: 200,
      cell: ({ getValue }: any) => (
        <span title={getValue()} style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '190px' }}>
          {getValue()}
        </span>
      ),
    },
    { id: 'customerId', header: 'Customer ID', accessorKey: 'customerId', size: 130 },
    {
      id: 'approvedAmount',
      header: 'Approved Amount',
      accessorKey: 'approvedAmount',
      size: 150,
      cell: ({ getValue }: any) => (
        <span style={{ fontWeight: 600 }}>{formatCurrency(getValue())}</span>
      ),
    },
    {
      id: 'goldRateAtApproval',
      header: 'Gold Rate/g',
      accessorKey: 'goldRateAtApproval',
      size: 130,
      cell: ({ getValue }: any) => {
        const val = getValue();
        return val ? formatCurrency(val) : '-';
      },
    },
    {
      id: 'totalNetWeight',
      header: 'Eligible Weight (g)',
      accessorKey: 'totalNetWeight',
      size: 130,
      cell: ({ getValue }: any) => (getValue() ?? 0).toFixed(3),
    },
    {
      id: 'totalGoldLoanAmount',
      header: 'Gold Loan Amt',
      accessorKey: 'totalGoldLoanAmount',
      size: 140,
      cell: ({ getValue }: any) => formatCurrency(getValue()),
    },
    {
      id: 'glCount',
      header: '# GLs',
      accessorKey: 'glCount',
      size: 80,
      cell: ({ getValue, row }: any) => {
        const count = getValue() as number;
        const rowId = (row.original as LoanMappingRecord).id;
        const isExpanded = expandedMappingId === rowId;
        return (
          <button
            onClick={(e) => { e.stopPropagation(); handleGlCountClick(rowId); }}
            style={{
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              minWidth: 28, height: 24, padding: '0 8px',
              borderRadius: 12, border: 'none', cursor: 'pointer',
              fontSize: '0.75rem', fontWeight: 700,
              background: isExpanded ? 'var(--primary)' : 'var(--primary-light, #e8f0fe)',
              color: isExpanded ? '#fff' : 'var(--primary)',
              transition: 'background 0.15s, color 0.15s',
            }}
            title={isExpanded ? 'Collapse GL details' : 'View GL details'}
          >
            {count}
          </button>
        );
      },
    },
    {
      id: 'businessUnitName',
      header: 'Business Unit',
      accessorKey: 'businessUnitName',
      size: 140,
      cell: ({ getValue }: any) => getValue() || '-',
    },
    {
      id: 'statusCode',
      header: 'Status',
      accessorKey: 'statusCode',
      size: 150,
      cell: ({ getValue }: any) => {
        const status = getValue();
        return (
          <span style={getStatusBadgeStyle(status)}>
            {(status ?? '').replace(/_/g, ' ')}
          </span>
        );
      },
    },
    {
      id: 'approvedBy',
      header: 'Approved By',
      accessorKey: 'approvedBy',
      size: 130,
      cell: ({ row }: any) => row.original.approvedByName || row.original.approvedBy,
    },
    {
      id: 'approvedAt',
      header: 'Approved At',
      accessorKey: 'approvedAt',
      size: 120,
      cell: ({ getValue }: any) => formatDate(getValue()),
    },
  ], [expandedMappingId, handleGlCountClick]);

  // ============================================================================
  // VIEW MODE & ACTION BUTTONS
  // ============================================================================

  const viewModeOptions: ViewModeOption[] = [
    { id: 'list', icon: <IconSystem.list size={16} />, label: 'List View', tooltip: 'List View' },
    { id: 'grid', icon: <IconSystem.grid size={16} />, label: 'Grid View', tooltip: 'Grid View', disabled: true },
    { id: 'calendar', icon: <IconSystem.calendar size={16} />, label: 'Calendar View', tooltip: 'Calendar View (Not Applicable)', disabled: true },
  ];

  const actionButtons: ActionButton[] = [
    {
      id: 'refresh',
      icon: <IconSystem.refresh size={18} />,
      label: 'Refresh',
      onClick: () => refetch(),
      tooltip: 'Refresh',
      variant: 'icon',
    },
    {
      id: 'export',
      icon: <IconSystem.download size={18} />,
      label: 'Export',
      onClick: () => showMessage('Export functionality will be available after API integration', 'info'),
      tooltip: 'Export',
      variant: 'icon',
    },
  ];

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <div className="module-master-container">
      <ModuleHeader
        visible={true}
        title="Loan Mappings"
        icon={<IconSystem.link size={20} />}
        description="View approved loan mappings with gold loan allocation details."
        searchConfig={{
          value: searchQuery,
          onChange: setSearchQuery,
          placeholder: 'Search by customer name or ID...',
        }}
        viewModeConfig={{
          currentMode: viewMode,
          modes: viewModeOptions,
          onChange: (mode) => setViewMode(mode),
        }}
        filterConfig={{
          onClick: () => setFilterOpen(!filterOpen),
          isActive: !!queryParams.statusCode,
        }}
        actions={actionButtons}
      />

      <div className="module-content">
        {apiError && mappings.length === 0 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '3rem', color: 'var(--danger)' }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '0.9rem', marginBottom: '0.5rem' }}>Failed to load loan mappings</div>
              <button className="btn btn-secondary" onClick={() => refetch()} style={{ fontSize: '0.8rem' }}>Retry</button>
            </div>
          </div>
        )}

        {apiLoading && mappings.length === 0 ? (
          <GridLoadingSkeleton columns={6} />
        ) : (
        <div style={{ display: 'flex', gap: '0', flex: 1, overflow: 'hidden' }}>
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Main grid */}
            <div style={{ flex: 1, overflow: 'hidden' }}>
              <SyutGrid
                data={mappings}
                columns={tableColumns}
                enableRowSelection
                enablePagination
                manualPagination
                totalCount={pagination.totalItems}
                currentPage={(queryParams.page ?? 1) - 1}
                pageSize={queryParams.pageSize ?? DEFAULT_PAGE_SIZE}
                onPageChange={handlePageChange}
                onPageSizeChange={handlePageSizeChange}
                enableColumnFilters={false}
                rowActions={getRowActions}
                onSelectionChange={setSelectedRows}
                isLoading={apiLoading}
                emptyMessage="No loan mappings found"
              />
            </div>

            {/* GL Detail Panel — appears below grid when a GL Count is clicked */}
            {expandedMappingId && (() => {
              const expandedRow = mappings.find((r) => r.id === expandedMappingId);
              return (
                <div style={{
                  flexShrink: 0,
                  borderTop: '2px solid var(--primary)',
                  background: 'var(--surface, #fff)',
                  padding: '0.75rem 1rem',
                  maxHeight: 260,
                  overflow: 'auto',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.6rem' }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: '0.85rem', color: 'var(--primary)' }}>GL Details</span>
                      {expandedRow && (
                        <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', marginLeft: '0.5rem' }}>
                          — {expandedRow.customerName}
                          {expandedRow.customerId ? ` (${expandedRow.customerId})` : ''}
                        </span>
                      )}
                    </div>
                    <button
                      className="search-panel-close"
                      onClick={() => setExpandedMappingId(null)}
                      title="Close GL details"
                    >
                      <IconSystem.x size={14} />
                    </button>
                  </div>

                  {glPanelLoading ? (
                    <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                      Loading GL details…
                    </div>
                  ) : glPanelRows.length === 0 ? (
                    <div style={{ padding: '0.5rem', color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                      No GL records found for this mapping.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                      <thead>
                        <tr style={{ background: 'var(--table-header-bg, #f4f6f9)' }}>
                          {['GL Account Number', 'GL Date', 'GL Customer ID', 'GL Customer Name', 'Eligible Weight (g)', 'Original Loan Amt (₹)', 'Alloc. Ratio (%)', 'GL Status'].map((h) => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {glPanelRows.map((gl) => (
                          <tr key={gl.id} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                            <td style={{ padding: '6px 10px' }}>{gl.glNumber || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{gl.loanDate ? gl.loanDate.split('T')[0] : '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{gl.glCustomerId || '—'}</td>
                            <td style={{ padding: '6px 10px' }}>{gl.glCustomerName || '—'}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{Number(gl.netWeight).toFixed(3)}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{formatCurrency(gl.originalLoanAmount)}</td>
                            <td style={{ padding: '6px 10px', fontWeight: 600 }}>{(Number(gl.allocationRatio) * 100).toFixed(2)}%</td>
                            <td style={{ padding: '6px 10px' }}>
                              <span style={getStatusBadgeStyle(gl.statusCode ?? '')}>{(gl.statusCode ?? '').replace(/_/g, ' ')}</span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })()}
          </div>
        </div>
        )}

        {/* Filter Panel */}
        <div className={`search-panel ${filterOpen ? '' : 'collapsed'}`}>
          <div className="search-panel-header">
            <h3 className="search-panel-title">Filters</h3>
            <button className="search-panel-close" onClick={() => setFilterOpen(false)} title="Close filters">
              <IconSystem.x size={14} />
            </button>
          </div>

          <div className="search-panel-content">
            <div className="filter-section">
              <SyutSelect
                label="Status"
                options={[
                  { value: '', label: 'All Status' },
                  { value: 'ACTIVE', label: 'Active' },
                  { value: 'PARTIALLY_CLOSED', label: 'Partially Closed' },
                  { value: 'FULLY_CLOSED', label: 'Fully Closed' },
                ]}
                value={statusFilter}
                onChange={(val: string | string[] | null) => setStatusFilter((val as string) || '')}
                placeholder="Search status..."
                isSearchable={true}
                isClearable={false}
                size="sm"
              />
            </div>
          </div>

          <div className="filter-actions">
            <button className="btn btn-secondary" onClick={handleClearFilters}>
              Clear
            </button>
            <button className="btn btn-primary" onClick={handleApplyFilters}>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* View Modal */}
      <LoanMappingViewModal
        isOpen={viewModalOpen}
        record={selectedRecord}
        loading={detailLoading}
        onClose={() => setViewModalOpen(false)}
        onRecordUpdated={handleRecordUpdated}
      />
    </div>
  );
};

export default LoanMappingScreen;
